/**
 * Messaging constants with no dependencies.
 *
 * Deliberately its own module: the tRPC router imports these too, and that
 * module reaches `~/env` through the Nest client. Pure presentation code has no
 * business dragging a server-only env schema in behind a number.
 *
 * Every cap here is MIRRORED from the API's own `messaging.constants.ts`, not
 * guessed. The server enforces all of them and rejects first — if these ever
 * disagree, the server wins and this file is the thing that is wrong. They exist
 * client-side only so a pick that cannot succeed is refused before it costs a
 * presign round trip, and phrased as something the user can act on rather than
 * as an opaque rejection from S3.
 */

/**
 * How the API presents EVERY staff sender and staff participant.
 *
 * One identity for the whole company, deliberately: which agent typed a reply is
 * not the customer's business, and a single consistent name is what makes the
 * support treatment worth anything as an anti-impersonation signal. Mirrored
 * here so the client can label a support thread whose `counterpart` the server
 * left null.
 */
export const SUPPORT_DISPLAY_NAME = "EZSpot Support";

export const CONVERSATION_PAGE_SIZE = 20;
export const CONVERSATION_PAGE_MAX_LIMIT = 50;
export const MESSAGE_PAGE_SIZE = 30;
export const MESSAGE_PAGE_MAX_LIMIT = 100;

export const MAX_ATTACHMENTS_PER_MESSAGE = 5;
export const MAX_ATTACHMENT_BYTES = 10 * 1024 * 1024;
export const MAX_ATTACHMENT_MB = MAX_ATTACHMENT_BYTES / (1024 * 1024);

/**
 * How long an attachment PUT may go with NO upload progress before it's
 * treated as stalled.
 *
 * A STALL timeout, not a total-duration one, deliberately: a fixed wall-clock
 * budget punishes a slow-but-flowing upload on a weak connection exactly as
 * much as a genuinely dead one, and `MAX_ATTACHMENT_BYTES` at real mobile
 * upload speeds can legitimately take longer than any fixed cap would allow.
 * The timer resets on every `progress` event instead, so only silence — not
 * slowness — trips it. Without it, a dropped connection pins one of the two
 * upload slots forever and `attachmentSendBlock` reports "uploading" with no
 * way out, since retry is only offered for `failed`.
 */
export const ATTACHMENT_UPLOAD_STALL_MS = 15 * 1000;

/**
 * How long the PUT may wait for S3's response once the body is fully sent.
 *
 * A separate, longer deadline rather than just clearing the stall timer at
 * that point — the body being sent doesn't mean the connection is still
 * alive, and with no timer armed at all a dead connection here would sit
 * until the OS's own TCP timeout (minutes), pinning an upload slot with no
 * way out: retry is only offered for `failed`, never `uploading`. Longer than
 * `ATTACHMENT_UPLOAD_STALL_MS` because this covers S3 actually processing the
 * object, not just bytes crossing the wire.
 */
export const ATTACHMENT_RESPONSE_TIMEOUT_MS = 30 * 1000;

/** The API's cap on one message body. */
export const MAX_MESSAGE_LENGTH = 4000;

/**
 * How long a rendered message must hold the viewport before it counts as read.
 *
 * A ratio of the SCROLLER, not of the message. A 4,000-character body — the
 * backend's cap — is taller than the thread pane on a laptop, so a threshold
 * measured against the element's own height could never be reached and the
 * conversation would sit unread forever on the one message the user is staring
 * at. `IntersectionObserver` measures intersection against the element, so tall
 * rows are handled explicitly rather than by threshold; see `useReadCursor`.
 */
export const READ_CURSOR_VISIBLE_RATIO = 0.6;
export const READ_CURSOR_DWELL_MS = 250;

/**
 * The predefined reasons `POST /messaging/conversations/:id/report` offers,
 * plus `other` for a reason none of them fit. Order is display order —
 * mirrored from the API's own `CONVERSATION_REPORT_REASONS`, not guessed.
 */
export const CONVERSATION_REPORT_REASONS = [
  "harassment_or_abuse",
  "inappropriate_content",
  "scam_or_fraud",
  "unsafe_situation",
  "spam",
  "other",
] as const;

export type ConversationReportReason =
  (typeof CONVERSATION_REPORT_REASONS)[number];

/** Mirrors the API's `CONVERSATION_REPORT_REASON_LABELS`. */
export const CONVERSATION_REPORT_REASON_LABELS: Record<
  ConversationReportReason,
  string
> = {
  harassment_or_abuse: "Harassment or abusive language",
  inappropriate_content: "Inappropriate content or photos",
  scam_or_fraud: "Scam, fraud, or payment request outside the app",
  unsafe_situation: "Unsafe or threatening situation",
  spam: "Spam or irrelevant messages",
  other: "Something else",
};

/** Mirrors the API's `REPORT_DETAILS_MAX_LENGTH`. Required, not just
 *  allowed, when `reason` is `other` — see `ReportConversationSchema`. */
export const REPORT_DETAILS_MAX_LENGTH = 2_000;
